import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { first } from "rxjs/operators";
import { environment } from "src/environments/environment";
import {
  AbsenceJustificationOption,
  CopyWeekResult,
  CreateVacationResult,
  HrExportFormat,
  HrMeta,
  HrTabCounts,
  HrUserProfile,
  HrUserRow,
  HrUsersMeta,
  HrOverview,
  JobOpening,
  JobOpeningStatus,
  JobOpeningsMeta,
  MyShift,
  PeerShift,
  ShiftAbsence,
  ShiftCopyPeriodResult,
  RequestVacationChangeResult,
  SaveJobOpeningPayload,
  ShiftAssignment,
  ShiftConflict,
  ShiftHistoryEntry,
  ShiftRequest,
  ShiftStoreUser,
  ShiftSummary,
  ShiftTemplate,
  ShiftWeek,
  ShiftsMeta,
  VacationCalendarResponse,
  VacationMeta,
  VacationPolicy,
  VacationRequest,
  VacationSettings,
  VacationSettingsUser,
  VacationChangeKind,
  VacationTeamUser,
  UpdateHrUserProfilePayload,
  HrEmergencyContact,
  HrHistoryCategory,
  HrHistoryEntry,
  CreateHrCompensationPayload,
  CreateHrDocumentPayload,
  HrCompensationResponse,
  HrContractsResponse,
  HrDocument,
  HrDocumentsResponse,
  HrEquipment,
  HrEquipmentResponse,
  HrTrainingResponse,
  HrUserAccess,
  ReturnHrEquipmentPayload,
  SaveHrContractPayload,
  SaveHrEquipmentPayload,
  SaveHrTrainingPayload,
  UpdateHrDocumentPayload,
} from "../models/hr.model";

@Injectable({ providedIn: "root" })
export class HrService {
  private readonly base = `${environment.apiUrl}/hr`;

  constructor(private http: HttpClient) {}

  private params(values: Record<string, any> = {}): HttpParams {
    let params = new HttpParams();
    Object.keys(values || {}).forEach((key) => {
      const value = values[key];
      if (value !== null && value !== undefined && value !== "") {
        params = params.set(key, value);
      }
    });
    return params;
  }

  /**
   * Que abas mostrar. Vem do servidor porque as permissões de perfil não
   * chegam ao `localStorage` que o `PermissionsService` lê.
   */
  getMeta() {
    return this.http
      .get<HrMeta>(`${this.base}/meta`, { withCredentials: true })
      .pipe(first());
  }

  /**
   * Os números das páginas de RH (pedidos por decidir, vagas...). O cabeçalho
   * de /hr actualiza-os ao navegar; o submenu do sidebar mostra-os em badge.
   */
  readonly tabCounts$ = new BehaviorSubject<Partial<HrTabCounts>>({});

  publishCounts(counts: Partial<HrTabCounts> | null | undefined): void {
    this.tabCounts$.next(counts ?? {});
  }

  /** O dia de hoje: só os blocos a que a pessoa tem acesso. */
  getOverview() {
    return this.http
      .get<HrOverview>(`${this.base}/overview`, { withCredentials: true })
      .pipe(first());
  }

  /* -------------------------------------------------------------- vagas */

  getJobOpeningsMeta() {
    return this.http
      .get<JobOpeningsMeta>(`${this.base}/job-openings/meta`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getJobOpenings(filters: { status?: JobOpeningStatus; search?: string } = {}) {
    return this.http
      .get<JobOpening[]>(`${this.base}/job-openings`, {
        params: this.params(filters),
        withCredentials: true,
      })
      .pipe(first());
  }

  getJobOpening(id: number) {
    return this.http
      .get<JobOpening>(`${this.base}/job-openings/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  createJobOpening(payload: SaveJobOpeningPayload) {
    return this.http
      .post<JobOpening>(`${this.base}/job-openings`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateJobOpening(id: number, payload: SaveJobOpeningPayload) {
    return this.http
      .patch<JobOpening>(`${this.base}/job-openings/${id}`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  publishJobOpening(id: number) {
    return this.http
      .patch<JobOpening>(`${this.base}/job-openings/${id}/publish`, {}, {
        withCredentials: true,
      })
      .pipe(first());
  }

  closeJobOpening(id: number) {
    return this.http
      .patch<JobOpening>(`${this.base}/job-openings/${id}/close`, {}, {
        withCredentials: true,
      })
      .pipe(first());
  }

  reopenJobOpening(id: number) {
    return this.http
      .patch<JobOpening>(`${this.base}/job-openings/${id}/reopen`, {}, {
        withCredentials: true,
      })
      .pipe(first());
  }

  deleteJobOpening(id: number) {
    return this.http
      .delete<{ deleted: boolean }>(`${this.base}/job-openings/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /* ------------------------------------------------------------- férias */

  getVacationsMeta() {
    return this.http
      .get<VacationMeta>(`${this.base}/vacations/meta`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getVacationRequests(filters: Record<string, any> = {}) {
    return this.http
      .get<VacationRequest[]>(`${this.base}/vacations/requests`, {
        params: this.params(filters),
        withCredentials: true,
      })
      .pipe(first());
  }

  getVacationCalendar(from: string, to: string, storeId?: number) {
    return this.http
      .get<VacationCalendarResponse>(`${this.base}/vacations/calendar`, {
        params: this.params({ from, to, storeId }),
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Ausências: as próprias (`mine`) ou as da equipa por justificar (`team`). */
  getAbsences(scope: "mine" | "team" = "mine") {
    return this.http
      .get<VacationRequest[]>(`${this.base}/vacations/absences`, {
        params: this.params({ scope }),
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Em nome de quem posso registar férias (MASTER, Diretor de Loja). */
  getVacationTeamUsers() {
    return this.http
      .get<VacationTeamUser[]>(`${this.base}/vacations/team-users`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Os pedidos do ano em Excel ou PDF, no âmbito de quem pede. */
  exportVacations(year: number, format: HrExportFormat, storeId?: number | null, lang?: string) {
    return this.http
      .get(`${this.base}/vacations/export`, {
        params: this.params({ year, format, storeId, lang }),
        withCredentials: true,
        responseType: "blob",
      })
      .pipe(first());
  }

  /** Definições de férias (só MASTER): regra por contrato e excepções. */
  getVacationSettings() {
    return this.http
      .get<VacationSettings>(`${this.base}/vacations/settings`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateVacationPolicy(payload: Partial<VacationPolicy>) {
    return this.http
      .patch<VacationPolicy>(`${this.base}/vacations/settings`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateVacationUserSettings(
    userId: string,
    payload: Pick<
      Partial<VacationSettingsUser>,
      "vacationApproverOverride" | "vacationDaysPerYear" | "carriedOverDays"
    >,
  ) {
    return this.http
      .patch(`${this.base}/vacations/settings/users/${userId}`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  createVacationRequest(payload: {
    startDate: string;
    endDate: string;
    note?: string;
    halfDay?: boolean;
    /** Em nome de outro (só quem pode registar pela equipa). */
    userId?: string;
    /** Baixa, licença... (só quem pode registar pela equipa). */
    type?: string;
    /** Só nas ausências: anexar já, pedir à pessoa ausente, ou dispensar. */
    justification?: AbsenceJustificationOption;
    /** Ausência de algumas horas (num só dia). */
    startTime?: string;
    endTime?: string;
  }) {
    return this.http
      .post<CreateVacationResult>(`${this.base}/vacations/requests`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Anexa (ou substitui) o documento que justifica uma ausência. */
  uploadAbsenceJustification(id: number, file: File) {
    const form = new FormData();
    form.append("file", file, file.name);
    return this.http
      .post<VacationRequest>(`${this.base}/vacations/requests/${id}/justification`, form, {
        withCredentials: true,
      })
      .pipe(first());
  }

  downloadAbsenceJustification(id: number) {
    return this.http
      .get(`${this.base}/vacations/requests/${id}/justification`, {
        withCredentials: true,
        responseType: "blob",
      })
      .pipe(first());
  }

  approveVacationRequest(id: number, reason?: string) {
    return this.http
      .patch<VacationRequest>(
        `${this.base}/vacations/requests/${id}/approve`,
        { reason },
        { withCredentials: true },
      )
      .pipe(first());
  }

  rejectVacationRequest(id: number, reason?: string) {
    return this.http
      .patch<VacationRequest>(
        `${this.base}/vacations/requests/${id}/reject`,
        { reason },
        { withCredentials: true },
      )
      .pipe(first());
  }

  cancelVacationRequest(id: number) {
    return this.http
      .patch<VacationRequest>(
        `${this.base}/vacations/requests/${id}/cancel`,
        {},
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Pedir para cancelar ou mudar férias aprovadas que ainda não começaram. */
  requestVacationChange(
    id: number,
    payload: {
      kind: VacationChangeKind;
      startDate?: string;
      endDate?: string;
      halfDay?: boolean;
      reason?: string;
    },
  ) {
    return this.http
      .patch<RequestVacationChangeResult>(
        `${this.base}/vacations/requests/${id}/change`,
        payload,
        { withCredentials: true },
      )
      .pipe(first());
  }

  approveVacationChange(id: number, reason?: string) {
    return this.http
      .patch<VacationRequest>(
        `${this.base}/vacations/requests/${id}/change/approve`,
        { reason },
        { withCredentials: true },
      )
      .pipe(first());
  }

  rejectVacationChange(id: number, reason?: string) {
    return this.http
      .patch<VacationRequest>(
        `${this.base}/vacations/requests/${id}/change/reject`,
        { reason },
        { withCredentials: true },
      )
      .pipe(first());
  }

  /* ------------------------------------------------------------ escalas */

  getShiftsMeta() {
    return this.http
      .get<ShiftsMeta>(`${this.base}/shifts/meta`, { withCredentials: true })
      .pipe(first());
  }

  getStoreUsers(storeId: number) {
    return this.http
      .get<ShiftStoreUser[]>(`${this.base}/shifts/stores/${storeId}/users`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getShiftTemplates(storeId: number) {
    return this.http
      .get<ShiftTemplate[]>(`${this.base}/shifts/templates`, {
        params: this.params({ storeId }),
        withCredentials: true,
      })
      .pipe(first());
  }

  createShiftTemplate(payload: Partial<ShiftTemplate>) {
    return this.http
      .post<ShiftTemplate>(`${this.base}/shifts/templates`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateShiftTemplate(id: number, payload: Partial<ShiftTemplate>) {
    return this.http
      .patch<ShiftTemplate>(`${this.base}/shifts/templates/${id}`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  deleteShiftTemplate(id: number) {
    return this.http
      .delete<ShiftTemplate>(`${this.base}/shifts/templates/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** A escala vista por pessoa: turnos, horas e dias no intervalo. */
  getShiftSummary(storeId: number, from: string, to: string) {
    return this.http
      .get<ShiftSummary>(`${this.base}/shifts/summary`, {
        params: this.params({ storeId, from, to }),
        withCredentials: true,
      })
      .pipe(first());
  }

  exportShifts(
    storeId: number,
    from: string,
    to: string,
    format: HrExportFormat,
    lang?: string,
    /** `month`: o PDF sai como calendário do mês. */
    layout?: "grid" | "month",
    /** O que entra: grid, people, absences, notes, conflicts. */
    parts?: string[],
  ) {
    return this.http
      .get(`${this.base}/shifts/export`, {
        params: this.params({ storeId, from, to, format, lang, layout, parts: parts?.length ? parts.join(",") : undefined }),
        withCredentials: true,
        responseType: "blob",
      })
      .pipe(first());
  }

  getShiftWeek(storeId: number, from: string, to: string) {
    return this.http
      .get<ShiftWeek>(`${this.base}/shifts/assignments`, {
        params: this.params({ storeId, from, to }),
        withCredentials: true,
      })
      .pipe(first());
  }

  createShiftAssignment(payload: {
    storeId: number;
    shiftTemplateId: number;
    userId: string;
    date: string;
    note?: string;
    notify?: boolean;
  }) {
    return this.http
      .post<{ assignment: ShiftAssignment; conflicts: string[] }>(
        `${this.base}/shifts/assignments`,
        payload,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Arrastar um turno para outra celula. */
  moveShiftAssignment(
    id: number,
    payload: { shiftTemplateId?: number; date?: string },
  ) {
    return this.http
      .patch<{ assignment: ShiftAssignment; conflicts: string[] }>(
        `${this.base}/shifts/assignments/${id}/move`,
        payload,
        { withCredentials: true },
      )
      .pipe(first());
  }

  deleteShiftAssignment(id: number) {
    return this.http
      .delete<ShiftAssignment>(`${this.base}/shifts/assignments/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  copyShiftWeek(payload: {
    storeId: number;
    fromWeekStart: string;
    toWeekStart: string;
    overwrite?: boolean;
  }) {
    return this.http
      .post<CopyWeekResult>(
        `${this.base}/shifts/assignments/copy-week`,
        payload,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getMyShifts(from: string, to: string) {
    return this.http
      .get<MyShift[]>(`${this.base}/shifts/my`, {
        params: this.params({ from, to }),
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Marcar-se num turno (CREATE_SHIFTS): o utilizador é quem está ligado. */
  createMyShift(payload: { storeId: number; shiftTemplateId: number; date: string }) {
    return this.http
      .post<{ assignment: ShiftAssignment; conflicts: string[] }>(
        `${this.base}/shifts/my`,
        payload,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Quantos turnos usam o modelo, para confirmar antes de o eliminar. */
  getShiftTemplateUsage(id: number) {
    return this.http
      .get<{ id: number; name: string; total: number; upcoming: number }>(
        `${this.base}/shifts/templates/${id}/usage`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Bloqueios e avisos da escala no intervalo. */
  getShiftConflicts(storeId: number, from: string, to: string) {
    return this.http
      .get<ShiftConflict[]>(`${this.base}/shifts/conflicts`, {
        params: this.params({ storeId, from, to }),
        withCredentials: true,
      })
      .pipe(first());
  }

  publishShifts(payload: { storeId: number; from: string; to: string; notify: boolean }, lang?: string) {
    return this.http
      .post<{ added: number; changed: number; removed: number; notified: number }>(
        `${this.base}/shifts/publish`,
        payload,
        { params: this.params({ lang }), withCredentials: true },
      )
      .pipe(first());
  }

  getShiftHistory(storeId: number) {
    return this.http
      .get<ShiftHistoryEntry[]>(`${this.base}/shifts/history`, {
        params: this.params({ storeId }),
        withCredentials: true,
      })
      .pipe(first());
  }

  /** `store`: os pedidos da loja (quem gere); `mine`: os meus e os que me fizeram. */
  getShiftRequests(scope: "store" | "mine", storeId?: number | null) {
    return this.http
      .get<ShiftRequest[]>(`${this.base}/shifts/requests`, {
        params: this.params({ scope, storeId }),
        withCredentials: true,
      })
      .pipe(first());
  }

  createShiftRequest(payload: {
    type: "SWAP" | "CHANGE" | "UNAVAILABLE";
    /** Sem turno só na indisponibilidade. */
    assignmentId?: number;
    peerId?: string;
    peerAssignmentId?: number;
    targetShiftTemplateId?: number;
    targetDate?: string;
    storeId?: number;
    startTime?: string;
    endTime?: string;
    reason?: string;
  }) {
    return this.http
      .post<ShiftRequest>(`${this.base}/shifts/requests`, payload, { withCredentials: true })
      .pipe(first());
  }

  respondShiftRequest(id: number, accept: boolean) {
    return this.http
      .patch<ShiftRequest>(`${this.base}/shifts/requests/${id}/respond`, { accept }, { withCredentials: true })
      .pipe(first());
  }

  decideShiftRequest(id: number, approve: boolean, comment?: string) {
    return this.http
      .patch<ShiftRequest>(`${this.base}/shifts/requests/${id}/decide`, { approve, comment }, { withCredentials: true })
      .pipe(first());
  }

  cancelShiftRequest(id: number) {
    return this.http
      .patch<ShiftRequest>(`${this.base}/shifts/requests/${id}/cancel`, {}, { withCredentials: true })
      .pipe(first());
  }

  /** Os turnos publicados de um colega, para escolher o que se quer na troca. */
  getPeerShifts(peerId: string) {
    return this.http
      .get<PeerShift[]>(`${this.base}/shifts/requests/peer-shifts`, {
        params: this.params({ peerId }),
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Várias pessoas num turno, num ou mais dias (o painel do turno). */
  bulkCreateShifts(payload: { storeId: number; shiftTemplateId: number; userIds: string[]; dates: string[]; note?: string }) {
    return this.http
      .post<{ created: number[]; skipped: Array<{ userId: string; date: string; reason: string }> }>(
        `${this.base}/shifts/assignments/bulk`,
        payload,
        { withCredentials: true },
      )
      .pipe(first());
  }

  setShiftCoverage(payload: { storeId: number; shiftTemplateId: number; dates: string[]; requiredStaff: number | null }) {
    return this.http
      .post(`${this.base}/shifts/coverage`, payload, { withCredentials: true })
      .pipe(first());
  }

  copyShiftPeriod(payload: {
    storeId: number;
    fromWeekStart: string;
    toWeekStart: string;
    userIds?: string[];
    weekdays?: number[];
    templateIds?: number[];
    mode: "add" | "replace";
    skipConflicts?: boolean;
    dryRun?: boolean;
  }) {
    return this.http
      .post<ShiftCopyPeriodResult>(`${this.base}/shifts/assignments/copy-period`, payload, { withCredentials: true })
      .pipe(first());
  }

  ignoreShiftWarning(payload: { storeId: number; conflictId: string; reason?: string }) {
    return this.http
      .post(`${this.base}/shifts/conflicts/ignore`, payload, { withCredentials: true })
      .pipe(first());
  }

  /** Férias e ausências da loja no período (a aba Ausências da escala). */
  getStoreAbsences(storeId: number, from: string, to: string) {
    return this.http
      .get<ShiftAbsence[]>(`${this.base}/shifts/absences`, {
        params: this.params({ storeId, from, to }),
        withCredentials: true,
      })
      .pipe(first());
  }

  /* ---------------------------------------------------------- utilizadores */

  /** Lojas do filtro, departamentos usados e se a pessoa pode editar fichas. */
  getUsersMeta() {
    return this.http
      .get<HrUsersMeta>(`${this.base}/users/meta`, { withCredentials: true })
      .pipe(first());
  }

  getUsers(filters: Record<string, any> = {}) {
    return this.http
      .get<HrUserRow[]>(`${this.base}/users`, {
        params: this.params(filters),
        withCredentials: true,
      })
      .pipe(first());
  }

  getUserProfile(id: string) {
    return this.http
      .get<HrUserProfile>(`${this.base}/users/${id}`, { withCredentials: true })
      .pipe(first());
  }

  updateUserProfile(id: string, payload: UpdateHrUserProfilePayload) {
    return this.http
      .patch<HrUserProfile>(`${this.base}/users/${id}`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /* ------------------------------------------------------ a própria ficha */

  /** Autosserviço: grava já os campos que o próprio pode alterar (fica no histórico). */
  updateMyProfile(payload: UpdateHrUserProfilePayload) {
    return this.http
      .patch<HrUserProfile>(`${this.base}/users/me`, payload, { withCredentials: true })
      .pipe(first());
  }

  /** `userId` null: a própria ficha. */
  updateEmergencyContacts(userId: string | null, contacts: HrEmergencyContact[]) {
    const path = userId ? `users/${userId}` : "users/me";
    return this.http
      .put<HrUserProfile>(`${this.base}/${path}/emergency-contacts`, { contacts }, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getUserHistory(userId: string | null, category?: HrHistoryCategory | null) {
    const path = userId ? `users/${userId}` : "users/me";
    return this.http
      .get<HrHistoryEntry[]>(`${this.base}/${path}/history`, {
        params: this.params({ category }),
        withCredentials: true,
      })
      .pipe(first());
  }

  /** O IBAN por inteiro: fica registado no histórico da ficha. */
  revealUserIban(userId: string) {
    return this.http
      .get<{ iban: string | null }>(`${this.base}/users/${userId}/iban`, { withCredentials: true })
      .pipe(first());
  }

  /* ------------------------------------------------- ficha: documentos */

  getUserDocuments(userId: string) {
    return this.http
      .get<HrDocumentsResponse>(`${this.base}/users/${userId}/documents`, { withCredentials: true })
      .pipe(first());
  }

  /** Multipart: os campos vão como texto, o ficheiro em `file`. */
  uploadUserDocument(userId: string, payload: CreateHrDocumentPayload, file: File) {
    const form = new FormData();
    form.append("category", payload.category);
    form.append("name", payload.name);
    if (payload.purpose) form.append("purpose", payload.purpose);
    if (payload.validUntil) form.append("validUntil", payload.validUntil);
    if (payload.visibility) form.append("visibility", payload.visibility);
    if (payload.requiresSignature) form.append("requiresSignature", "true");
    form.append("file", file, file.name);
    return this.http
      .post<HrDocument>(`${this.base}/users/${userId}/documents`, form, { withCredentials: true })
      .pipe(first());
  }

  updateUserDocument(userId: string, documentId: number, payload: UpdateHrDocumentPayload) {
    return this.http
      .patch<HrDocument>(`${this.base}/users/${userId}/documents/${documentId}`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  replaceUserDocumentFile(userId: string, documentId: number, file: File) {
    const form = new FormData();
    form.append("file", file, file.name);
    return this.http
      .post<HrDocument>(`${this.base}/users/${userId}/documents/${documentId}/version`, form, {
        withCredentials: true,
      })
      .pipe(first());
  }

  deleteUserDocument(userId: string, documentId: number) {
    return this.http
      .delete(`${this.base}/users/${userId}/documents/${documentId}`, { withCredentials: true })
      .pipe(first());
  }

  downloadUserDocument(userId: string, documentId: number) {
    return this.http
      .get(`${this.base}/users/${userId}/documents/${documentId}/file`, {
        withCredentials: true,
        responseType: "blob",
      })
      .pipe(first());
  }

  /* ----------------------------------------------- ficha: equipamentos */

  getUserEquipment(userId: string) {
    return this.http
      .get<HrEquipmentResponse>(`${this.base}/users/${userId}/equipment`, { withCredentials: true })
      .pipe(first());
  }

  saveUserEquipment(userId: string, itemId: number | null, payload: SaveHrEquipmentPayload) {
    const url = `${this.base}/users/${userId}/equipment`;
    const request = itemId
      ? this.http.put<HrEquipment>(`${url}/${itemId}`, payload, { withCredentials: true })
      : this.http.post<HrEquipment>(url, payload, { withCredentials: true });
    return request.pipe(first());
  }

  returnUserEquipment(userId: string, itemId: number, payload: ReturnHrEquipmentPayload) {
    return this.http
      .patch<HrEquipment>(`${this.base}/users/${userId}/equipment/${itemId}/return`, payload, {
        withCredentials: true,
      })
      .pipe(first());
  }

  deleteUserEquipment(userId: string, itemId: number) {
    return this.http
      .delete(`${this.base}/users/${userId}/equipment/${itemId}`, { withCredentials: true })
      .pipe(first());
  }

  /* --------------------------------------- ficha: contratos e remuneração */

  getUserContracts(userId: string) {
    return this.http
      .get<HrContractsResponse>(`${this.base}/users/${userId}/contracts`, { withCredentials: true })
      .pipe(first());
  }

  saveUserContract(userId: string, contractId: number | null, payload: SaveHrContractPayload) {
    const url = `${this.base}/users/${userId}/contracts`;
    const request = contractId
      ? this.http.put<{ id: number }>(`${url}/${contractId}`, payload, { withCredentials: true })
      : this.http.post<{ id: number }>(url, payload, { withCredentials: true });
    return request.pipe(first());
  }

  deleteUserContract(userId: string, contractId: number) {
    return this.http
      .delete(`${this.base}/users/${userId}/contracts/${contractId}`, { withCredentials: true })
      .pipe(first());
  }

  getUserCompensation(userId: string) {
    return this.http
      .get<HrCompensationResponse>(`${this.base}/users/${userId}/compensation`, { withCredentials: true })
      .pipe(first());
  }

  createUserCompensation(userId: string, payload: CreateHrCompensationPayload) {
    return this.http
      .post<{ id: number }>(`${this.base}/users/${userId}/compensation`, payload, { withCredentials: true })
      .pipe(first());
  }

  deleteUserCompensation(userId: string, changeId: number) {
    return this.http
      .delete(`${this.base}/users/${userId}/compensation/${changeId}`, { withCredentials: true })
      .pipe(first());
  }

  /* --------------------------------------------------- ficha: formação */

  getUserTrainings(userId: string) {
    return this.http
      .get<HrTrainingResponse>(`${this.base}/users/${userId}/trainings`, { withCredentials: true })
      .pipe(first());
  }

  saveUserTraining(userId: string, itemId: number | null, payload: SaveHrTrainingPayload) {
    const url = `${this.base}/users/${userId}/trainings`;
    const request = itemId
      ? this.http.put<{ id: number }>(`${url}/${itemId}`, payload, { withCredentials: true })
      : this.http.post<{ id: number }>(url, payload, { withCredentials: true });
    return request.pipe(first());
  }

  deleteUserTraining(userId: string, itemId: number) {
    return this.http
      .delete(`${this.base}/users/${userId}/trainings/${itemId}`, { withCredentials: true })
      .pipe(first());
  }

  /* ---------------------------------------------------- ficha: acessos */

  getUserAccess(userId: string) {
    return this.http
      .get<HrUserAccess>(`${this.base}/users/${userId}/access`, { withCredentials: true })
      .pipe(first());
  }
}
